export const LICENSE_STORAGE_KEY = "user-license";

export const normalizeModuleKey = (value) => (value ? String(value).trim().toLowerCase() : null);

const normalizeLicenseStatus = (value) => {
  const normalized = value ? String(value).trim().toLowerCase() : null;
  if (!normalized) return null;
  if (["active", "read_only", "inactive"].includes(normalized)) {
    return normalized;
  }
  return null;
};

const buildModuleAccessEntry = (moduleKey, rawValue, licensedModuleSet, fallbackStatus = null) => {
  const normalizedModuleKey = normalizeModuleKey(moduleKey);
  if (!normalizedModuleKey) return null;

  const source = rawValue && typeof rawValue === "object" ? rawValue : {};
  const status =
    normalizeLicenseStatus(source.status || source.effective_status || source.access || rawValue) ||
    normalizeLicenseStatus(fallbackStatus) ||
    (licensedModuleSet.has(normalizedModuleKey) ? "active" : "inactive");

  const enabled =
    Object.prototype.hasOwnProperty.call(source, "enabled")
      ? Boolean(source.enabled)
      : status === "active" || status === "read_only" || licensedModuleSet.has(normalizedModuleKey);
  const readOnly =
    Object.prototype.hasOwnProperty.call(source, "read_only")
      ? Boolean(source.read_only)
      : status === "read_only";

  return {
    status: enabled ? (readOnly ? "read_only" : status === "inactive" ? "active" : status) : "inactive",
    enabled: enabled && status !== "inactive",
    read_only: readOnly || status === "read_only",
  };
};

export const normalizeLicenseSnapshot = (input) => {
  if (!input || typeof input !== "object") {
    return null;
  }

  const source = input.license && typeof input.license === "object" ? input.license : input;
  const licensedModulesRaw = Array.isArray(source.licensed_modules)
    ? source.licensed_modules
    : Array.isArray(source.licensedModules)
      ? source.licensedModules
      : [];
  const licensedModules = Array.from(
    new Set(licensedModulesRaw.map((item) => normalizeModuleKey(item)).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right));
  const licensedModuleSet = new Set(licensedModules);

  const fallbackStatus =
    normalizeLicenseStatus(source.effective_status || source.effectiveStatus || source.status) || null;
  const rawModuleAccess =
    source.module_access && typeof source.module_access === "object"
      ? source.module_access
      : source.moduleAccess && typeof source.moduleAccess === "object"
        ? source.moduleAccess
        : {};

  const moduleAccess = {};
  Object.entries(rawModuleAccess).forEach(([moduleKey, rawValue]) => {
    const entry = buildModuleAccessEntry(moduleKey, rawValue, licensedModuleSet, fallbackStatus);
    if (entry) {
      moduleAccess[normalizeModuleKey(moduleKey)] = entry;
    }
  });

  licensedModules.forEach((moduleKey) => {
    if (!moduleAccess[moduleKey]) {
      moduleAccess[moduleKey] = buildModuleAccessEntry(moduleKey, "active", licensedModuleSet, fallbackStatus);
    }
  });

  return {
    package_key: source.package_key || source.packageKey || null,
    status: normalizeLicenseStatus(source.status) || fallbackStatus || "inactive",
    effective_status: fallbackStatus || normalizeLicenseStatus(source.status) || "inactive",
    expires_at: source.expires_at || source.expiresAt || null,
    is_expired: Boolean(source.is_expired ?? source.isExpired ?? false),
    licensed_modules: licensedModules,
    module_access: moduleAccess,
  };
};

export const extractLicenseSnapshotFromTokenPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return normalizeLicenseSnapshot({
    package_key: payload.licensePackage || payload.license_package || null,
    effective_status: payload.licenseStatus || payload.license_status || null,
    licensed_modules: payload.licensedModules || payload.licensed_modules || [],
    module_access: payload.moduleAccess || payload.module_access || {},
  });
};

export const storeLicenseSnapshot = (input) => {
  const snapshot = normalizeLicenseSnapshot(input);
  if (!snapshot) {
    localStorage.removeItem(LICENSE_STORAGE_KEY);
    return null;
  }
  localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(snapshot));
  return snapshot;
};

export const clearLicenseSnapshot = () => {
  localStorage.removeItem(LICENSE_STORAGE_KEY);
};

export const readStoredLicenseSnapshot = (fallbackTokenPayload = null) => {
  try {
    const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const normalized = normalizeLicenseSnapshot(parsed);
      if (normalized) {
        return normalized;
      }
    }
  } catch (_) {
    // fallback below
  }
  return extractLicenseSnapshotFromTokenPayload(fallbackTokenPayload);
};

export const userHasAdminLicenseBypass = (roleKeys = []) => {
  const values = Array.isArray(roleKeys) ? roleKeys : Array.from(roleKeys || []);
  return values.some((roleKey) => String(roleKey || "").trim().toLowerCase() === "admin");
};

export const canAccessModule = (
  moduleKey,
  { roleKeys = [], licenseSnapshot = null, fallbackIfUnknown = false } = {}
) => {
  const normalizedModuleKey = normalizeModuleKey(moduleKey);
  if (!normalizedModuleKey) {
    return true;
  }
  if (userHasAdminLicenseBypass(roleKeys)) {
    return true;
  }
  const snapshot = normalizeLicenseSnapshot(licenseSnapshot);
  if (!snapshot) {
    return Boolean(fallbackIfUnknown);
  }
  return Boolean(snapshot.module_access?.[normalizedModuleKey]?.enabled);
};

export const isModuleReadOnly = (
  moduleKey,
  { roleKeys = [], licenseSnapshot = null } = {}
) => {
  const normalizedModuleKey = normalizeModuleKey(moduleKey);
  if (!normalizedModuleKey || userHasAdminLicenseBypass(roleKeys)) {
    return false;
  }
  const snapshot = normalizeLicenseSnapshot(licenseSnapshot);
  if (!snapshot) {
    return false;
  }
  return Boolean(snapshot.module_access?.[normalizedModuleKey]?.read_only);
};
